'use client'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton
} from '@mui/material'
import { faTimes } from '@fortawesome/pro-regular-svg-icons'
import Button from 'src/components/era-package/forms/Button'
import Buttons from 'src/components/era-package/era/Buttons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  header,
  entryInfo,
  entryStatus,
  liquidation,
  dutyTaxFeeInterest,
  bondAndSurety,
  protest,
  billing,
  collection,
  classCodeAmount,
  suretySpecificBilling,
  optionsMap
} from 'src/views/abi/fields/es-query-response'
import RenderFormWithRHF from 'src/components/era-package/forms/RenderFormWithRHF'
import ParentCard from 'src/components/era-package/shared/ParentCard'
import DataTableSection from 'src/components/era-package/forms/DataTableSection'

const Wrapper = ({ readOnly, children }) => {
  if (readOnly) return <>{children}</>
  return (
    <ParentCard title='Trade Partner Details'>
      <Alert severity='info' sx={{ mb: 2 }}>
        <AlertTitle>Note</AlertTitle>
        This form captures all required data elements. Fields marked with{' '}
        <Box component='span' color='error.main'>
          *
        </Box>{' '}
        are mandatory.
      </Alert>
      {children}
    </ParentCard>
  )
}

const QueryResponseModal = ({
  title = 'Details',
  open,
  onClose,
  responseData,
  fields
}) => {
  const [fetchedData, setFetchedData] = useState()
  const dialogRef = useRef(null)

  // Initialize form data when modal opens or responseData changes
  useEffect(() => {
    if (responseData) {
      setFetchedData(responseData)
    }
  }, [responseData, open])

  // Handle keyboard navigation (consistent with EditModal)
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose()
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    }
  }

  const formSections = useMemo(
    () => [
      {
        title: 'Header',
        fields: header,
        disableGutters: true
      },
      {
        title: 'Entry Info',
        fields: entryInfo,
        disableGutters: true
      },
      {
        title: 'Entry Status',
        fields: entryStatus,
        disableGutters: true
      },
      {
        title: 'Liquidation',
        fields: liquidation,
        disableGutters: true
      },
      {
        title: 'Duty/Tax/Fee/Interest',
        fields: dutyTaxFeeInterest,
        disableGutters: true
      },
      {
        title: 'Bond and Surety',
        type: 'table',
        name: `bondAndSuretyList`,
        tableComponent: DataTableSection,
        fields: bondAndSurety,
        tableFields: bondAndSurety,
        disableGutters: true
      },
      {
        title: 'Protest',
        fields: protest,
        type: 'table',
        name: `protestList`,
        tableComponent: DataTableSection,
        tableFields: protest,
        disableGutters: true
      },
      {
        title: 'Billing',
        type: 'table',
        name: `billingList`,
        tableComponent: DataTableSection,
        fields: billing,
        tableFields: billing,
        disableGutters: true
      },
      {
        title: 'Collections Processing Date and  Amount',
        type: 'table',
        name: `collectionList`,
        tableComponent: DataTableSection,
        fields: collection,
        tableFields: collection,
        disableGutters: true
      },
      {
        title: 'Duty/Tax/Fees with Class Code',
        type: 'table',
        name: `classCodeAmountList`,
        tableComponent: DataTableSection,
        fields: classCodeAmount,
        tableFields: classCodeAmount,
        disableGutters: true
      },
      {
        title: 'Surety-Specific Billing Data',
        type: 'table',
        name: `suretySpecficBillingList`,
        tableComponent: DataTableSection,
        fields: suretySpecificBilling,
        tableFields: suretySpecificBilling,
        disableGutters: true
      }
    ],
    []
  )

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  // Don't render if no data
  if (!responseData || !fields) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      ref={dialogRef}
      PaperProps={{
        sx: {
          maxHeight: '90vh'
        }
      }}
      // Same focus management as EditModal
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      onKeyDown={handleKeyDown}
      aria-labelledby='response-modal-title'
      aria-describedby='response-modal-description'
      role='dialog'
    >
      <DialogTitle
        id='response-modal-title'
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        {title}
        <IconButton
          aria-label={`Close ${title.toLowerCase()} modal`}
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500]
          }}
          title='Close modal'
        >
          <Box
            component={FontAwesomeIcon}
            icon={faTimes}
            sx={{ width: '1em' }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers id='response-modal-description'>
        <Box sx={{ py: 2 }}>
          <Wrapper readOnly={true}>
            <RenderFormWithRHF
              formSections={formSections}
              allFields={allFields}
              optionsMap={optionsMap}
              submitLabel=''
              onSubmit={{}}
              sx={{ border: 0 }}
              formConfig={{}}
              initialData={fetchedData}
              readOnly={true}
              hideSave={true}
              basicForm={true}
            />
          </Wrapper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 3 }}>
        <Buttons
          advance={
            <Button
              onClick={onClose}
              variant='contained'
              title={`Close ${title.toLowerCase()}`}
            >
              Close
            </Button>
          }
        />
      </DialogActions>
    </Dialog>
  )
}

export default QueryResponseModal
